import { createSelector } from 'reselect'
import { getVal, isEmpty } from 'react-redux-firebase'
import { matchPath } from 'react-router'
import { routes } from 'variables'
import {
  firebaseSelector,
  firebaseOrderedSelector,
  profileSelector,
  userIdSelector
} from './baseSelectors'

export const planSelector = createSelector(
  [profileSelector],
  profile => profile && profile.subscription && profile.subscription.planId
)

export const pathnameSelector = state =>
  state.router.location && state.router.location.pathname

export const locationSelector = createSelector([pathnameSelector], pathname => {
  const matchToParams = match => ({
    boardId: match.params.boardId,
    folderId: match.params.folderId,
    shareId: match.params.shareId && match.params.shareId.substr(2),
    embed: !!match.params.embed,
    blockId: match.params.blockId
  })

  let match = matchPath(pathname, { path: routes.board, exact: true })
  if (match) return matchToParams(match)

  match = matchPath(pathname, { path: routes.block })
  if (match) return matchToParams(match)

  match = matchPath(pathname, { path: routes.folder })
  if (match) return matchToParams(match)

  return {
    boardId: null,
    blockId: null
  }
})

export const currentBoardIdSelector = createSelector(
  [locationSelector],
  location => location.boardId
)
export const currentBlockIdSelector = createSelector(
  [locationSelector],
  location => location.blockId
)
export const currentFolderIdSelector = createSelector(
  [locationSelector],
  location => location.folderId
)
export const shareKeySelector = createSelector(
  [locationSelector],
  (location, shareKey) => location.shareId
)
export const embedModeSelector = createSelector(
  [locationSelector],
  location => location.embed
)

export const currentBoardSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => boardId ? getVal(firebase, `boards/${boardId}`) : null
)

export const currentBoardBlocksSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => getVal(firebase, `blocks/${boardId}`)
)

export const currentBoardSectionsSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => getVal(firebase, `sections/${boardId}`)
)

export const currentBoardColorSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => getVal(firebase, `boards/${boardId}/color`)
)

export const currentFolderSelector = createSelector(
  [firebaseSelector, currentFolderIdSelector],
  (firebase, folderId) => {
    if (folderId) return getVal(firebase, `folders/${folderId}`)
  }
)

export const storedShareSelector = state => state.share.storedShare
export const shareIdSelector = createSelector(
  [
    currentBoardIdSelector,
    currentFolderIdSelector,
    shareKeySelector,
    storedShareSelector
  ],
  (boardId, folderId, shareKey, storedShare) => {
    return (
      storedShare || {
        id: shareKey || 'open',
        itemId: boardId || folderId
      }
    )
  }
)

export const shareSelector = createSelector(
  [firebaseSelector, shareIdSelector],
  (firebase, shareId) => {
    const shares = getVal(firebase, `shares/${shareId.itemId}/`)
    if (!shares) return shares
    const share = shares[shareId.id || 'open'] || null
    if(share && shareId.stored)
      share.stored = true
    return share
  }
)

export const currentBlockSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector, currentBlockIdSelector],
  (firebase, boardId, blockId) => {
    if (!boardId) return null
    return getVal(firebase, `blocks/${boardId}/${blockId}`)
  }
)

export const modeSelector = state => state.app.mode
export const editModeSelector = state => state.app.mode === 'edit'
export const textEditBlockSelector = state => state.board.textEditMode
export const placementModeSelector = state => state.board.placementMode
export const placementWidthSelector = state => state.queue.columns
export const queueOpenSelector = state => state.toggles.queue
export const queueIndexSelector = state => state.queue.selectedIndex

export const queueSelector = createSelector(
  [firebaseOrderedSelector, userIdSelector],
  (firebase, userId) => userId && getVal(firebase, `queues/${userId}`)
)

export const queueLengthSelector = createSelector(
  [queueSelector],
  queue => (queue ? queue.length : 0)
)

export const currentQueueItemSelector = createSelector(
  [queueSelector, queueIndexSelector],
  (queue, queueIndex) => {
    if (isEmpty(queue)) return null
    const index = queue.length - queueIndex - 1
    // it's possible we will place the last item in the queue, shortening it before
    // decrementing queueIndex causing an index of -1 Math.max prevents problems
    // We do this so we can be sure the block is placed before previewing the next one
    // otherwise the preview may vanish before the placed block appears causing a flicker
    return queue[Math.max(index, 0)].value
  }
)

export const placementBlockSelector = createSelector(
  [
    editModeSelector,
    placementModeSelector,
    queueOpenSelector,
    currentQueueItemSelector
  ],
  (editMode, placementMode, queueOpen, queueItem) => {
    if (!editMode) return
    if (placementMode === 'color') {
      return {
        type: 'color',
        name: 'Color',
        style: { background: '#ccc' }
      }
    } else if (placementMode === 'text') {
      return {
        name: 'Text',
        text: {
          text: '',
          align: 'left',
          color: 'black',
          size: 0.6
        },
        height: 0
      }
    }
    if (queueOpen) return queueItem
  }
)

export const uploadsSelector = state => state.uploads
export const pendingUploadsSelector = createSelector(
  [uploadsSelector],
  uploads => Object.values(uploads).some(upload => upload && !upload.uploaded)
)

export const hasMouseSelector = state => state.app.hasMouse
